"use client";

import { useState, useEffect, useCallback } from "react";
import ClientOnly from "./ClientOnly";

export default function Background() {
  const [scrollY, setScrollY] = useState(0);

  const [stars, setStars] = useState<
    Array<{
      top: number;
      left: number;
      size: number;
      opacity: number;
      delay: number;
      type: "star" | "shooting" | "nebula";
    }>
  >([]);

  useEffect(() => {
    setStars(
      Array.from({ length: 30 }, (_, i) => ({
        top: Math.random() * 100,
        left: Math.random() * 100,
        size: Math.random() * 3 + 1,
        opacity: Math.random() * 0.8 + 0.2,
        delay: Math.random() * 5,
        type: i < 20 ? "shooting" : "nebula",
      }))
    );
  }, []);

  const handleScroll = useCallback(() => {
    let ticking = false;

    return () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };
  }, []);

  useEffect(() => {
    const scrollHandler = handleScroll();
    window.addEventListener("scroll", scrollHandler, { passive: true });
    return () => {
      window.removeEventListener("scroll", scrollHandler);
    };
  }, [handleScroll]);

  const moonStyle = {
    transform: `translate3d(${200 - scrollY * 0.05}px, ${
      -100 + scrollY * 0.02
    }px, 0)`,
    willChange: "transform",
  };

  return (
    <>
      <ClientOnly>
        <div className="fixed inset-0 z-0 overflow-hidden">
          {/* Nebula background */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-[20%] left-[10%] w-[300px] h-[200px] rounded-full bg-gradient-to-r from-purple-500/30 to-blue-500/30 blur-xl"></div>
            <div className="absolute top-[60%] right-[20%] w-[250px] h-[180px] rounded-full bg-gradient-to-r from-cyan-500/20 to-green-500/20 blur-xl"></div>
            <div className="absolute bottom-[30%] left-[50%] w-[200px] h-[150px] rounded-full bg-gradient-to-r from-pink-500/25 to-red-500/25 blur-xl"></div>
          </div>

          {/* Shooting stars and nebula elements */}
          {stars.map((star, i) => (
            <div
              key={i}
              className={`absolute rounded-full ${
                star.type === "shooting"
                  ? "bg-gradient-to-r from-white to-transparent"
                  : "bg-gradient-to-r from-purple-400 to-blue-400"
              }`}
              style={{
                top: `${star.top}%`,
                left: `${star.left}%`,
                width:
                  star.type === "shooting"
                    ? `${star.size * 3}px`
                    : `${star.size}px`,
                height: `${star.size}px`,
                opacity: star.opacity,
                animation:
                  star.type === "shooting"
                    ? `shootingStar ${Math.random() * 4 + 4}s infinite ${
                        star.delay
                      }s`
                    : "none",
                willChange: "opacity, transform",
                transform: star.type === "shooting" ? "rotate(-45deg)" : "none",
              }}
            />
          ))}
        </div>

        {/* Enhanced moon with craters and glow */}
        <div
          className="fixed right-0 top-0 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-gray-700 via-gray-800 to-gray-900 z-0 opacity-80"
          style={moonStyle}
        >
          {/* Moon glow effect */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-gray-600/20 to-transparent blur-2xl"></div>

          {/* Enhanced craters */}
          <div className="absolute top-[30%] left-[20%] w-[50px] h-[50px] rounded-full bg-gray-800 opacity-60 shadow-inner"></div>
          <div className="absolute top-[50%] left-[40%] w-[70px] h-[70px] rounded-full bg-gray-800 opacity-60 shadow-inner"></div>
          <div className="absolute top-[20%] left-[60%] w-[40px] h-[40px] rounded-full bg-gray-800 opacity-60 shadow-inner"></div>
          <div className="absolute top-[70%] left-[30%] w-[35px] h-[35px] rounded-full bg-gray-800 opacity-50 shadow-inner"></div>
          <div className="absolute top-[40%] left-[70%] w-[45px] h-[45px] rounded-full bg-gray-800 opacity-55 shadow-inner"></div>
        </div>
      </ClientOnly>

      <style jsx global>{`
        @keyframes twinkle {
          0%,
          100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.1);
          }
        }

        @keyframes shootingStar {
          0% {
            opacity: 0;
            transform: translateX(-100px) translateY(0) rotate(-45deg);
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            transform: translateX(100vw) translateY(100vh) rotate(-45deg);
          }
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
      `}</style>
    </>
  );
}
